import { z } from 'zod'

const PRODUCT_MANAGEMENT_TAB_VALUES = ['map', 'kanban'] as const
export type ProductManagementTabValue = (typeof PRODUCT_MANAGEMENT_TAB_VALUES)[number]

// Identifiant d'une brique ou d'une fonctionnalité au format qu'accepte le service (8-4-4-4-12
// chiffres hexadécimaux) : rien d'autre n'atteint le chemin de `GET /scoping/components/:id` (un
// `..%2F` forgé partirait vers une autre route du service).
const entityIdParam = z.guid().optional().catch(undefined)

/**
 * Onglet actif, brique ouverte sur la carte et fonctionnalité ouverte dans le Kanban, portés par
 * l'URL : ils survivent au rafraîchissement et au partage de lien. Les liens des relances par SMS
 * ouvrent ainsi la description d'une brique (`?tab=map&component=<id>`), dans son volet en vue
 * lecteur ; un lien `?tab=kanban&ticket=<id>` ouvre le volet de la brique de cette fonctionnalité,
 * sa fiche dépliée (M16).
 */
export const productManagementSearchSchema = z.object({
  tab: z.enum(PRODUCT_MANAGEMENT_TAB_VALUES).catch('map'),
  component: entityIdParam,
  ticket: entityIdParam,
})

type ProductManagementSearch = z.infer<typeof productManagementSearchSchema>

/** Changer d'onglet ferme la brique ou le ticket ouvert : chacun n'appartient qu'à son onglet. */
export const withTab = <Search extends ProductManagementSearch>(
  search: Search,
  tab: ProductManagementTabValue,
): Search => ({ ...search, tab, component: undefined, ticket: undefined })

/** Ouvre une brique de la carte dans son volet, ou le ferme (`undefined`). */
export const withOpenComponent = <Search extends ProductManagementSearch>(
  search: Search,
  component: string | undefined,
): Search => ({ ...search, component })

/** Ouvre un ticket du Kanban, ou le ferme (`undefined`). */
export const withOpenTicket = <Search extends ProductManagementSearch>(
  search: Search,
  ticket: string | undefined,
): Search => ({ ...search, ticket })
